
import { useState, useCallback, useMemo, useRef, useEffect } from 'react';

interface ProductCardProps {
  product: {
    id: string;
    name: string;
    price: number;
    image: string;
    category: string;
    description: string;
    stock: number;
    rating: number;
    reviews: number;
    verkaufseinheit?: string;
    mindestmengeStueck?: number;
    isB2B?: boolean;
    sku?: string;
    stueckProVE?: number;
    veProKarton?: number;
    kartonProPalette?: number;
    preisProStueck?: number;
    mwstSatz?: number;
    // NEUE FELDER - EXAKT Ihre Spezifikation
    preis_pro_verpackungseinheit?: number;
    stueck_pro_ve?: number;
    price_pro_stueck?: number;
    ve_pro_karton?: number;
    karton_pro_palette?: number;
  };
  onAddToCart: (product: any, quantity: number) => void;
}

export default function ProductCard({ product, onAddToCart }: ProductCardProps) {
  const [quantity, setQuantity] = useState(1);
  const [isAdding, setIsAdding] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [showImageModal, setShowImageModal] = useState(false);

  // KORRIGIERTE B2B-Preislogik - SICHERE FALLBACKS
  const hasB2BPricing = product.preis_pro_verpackungseinheit && product.stueck_pro_ve;
  
  // SICHERE PREISBERECHNUNG mit Fallbacks
  const safePrice = product.preis_pro_verpackungseinheit || product.price || 0;
  const safePricePerPiece = product.price_pro_stueck || product.preisProStueck || 0;
  const safeVEQuantity = product.stueck_pro_ve || product.stueckProVE || 1;
  const safeVEPerCarton = product.ve_pro_karton || product.veProKarton || null;
  const safeCartonPerPallet = product.karton_pro_palette || product.kartonProPalette || null;
  
  const displayPrice = safePrice;
  const priceUnit = hasB2BPricing ? 'pro Verpackungseinheit' : 'pro Stück';
  const minQuantity = hasB2BPricing ? 1 : (product.mindestmengeStueck || 1);

  const handleAddToCart = async () => {
    if (isAdding) return;
    
    setIsAdding(true);
    try {
      await onAddToCart(product, quantity);
    } finally {
      setIsAdding(false);
    }
  };

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity >= minQuantity) {
      setQuantity(newQuantity);
    }
  };

  const handleImageClick = () => {
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
  };

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100 group">
        {/* Produktbild - Mobile optimiert */}
        <div className="relative overflow-hidden bg-gray-50 cursor-pointer" onClick={handleImageClick}>
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-40 sm:h-48 object-contain object-center group-hover:scale-105 transition-transform duration-300 p-2"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = 'https://via.placeholder.com/400x300?text=Bild+nicht+verfügbar';
            }}
          />
          
          {/* Zoom-Icon beim Hover - Mobile angepasst */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 rounded-full p-2">
              <i className="ri-zoom-in-line text-lg sm:text-xl text-gray-700"></i>
            </div>
          </div>
          
          {/* B2B Badge - Mobile optimiert */}
          {hasB2BPricing && (
            <div className="absolute top-2 left-2">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                B2B
              </span>
            </div>
          )}

          {/* Lagerbestand Badge - Mobile optimiert */}
          <div className="absolute top-2 right-2">
            {product.stock === 0 ? (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                <span className="hidden sm:inline">Ausverkauft</span>
                <span className="sm:hidden">Aus</span>
              </span>
            ) : product.mindestmengeStueck && product.stock < product.mindestmengeStueck ? (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                <span className="hidden sm:inline">Nicht verfügbar</span>
                <span className="sm:hidden">N/A</span>
              </span>
            ) : product.stock <= 10 ? (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                <span className="hidden sm:inline">Wenige verfügbar</span>
                <span className="sm:hidden">Wenige</span>
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                <span className="hidden sm:inline">Verfügbar</span>
                <span className="sm:hidden">OK</span>
              </span>
            )}
          </div>
        </div>

        <div className="p-4 sm:p-6">
          {/* Produktname - Mobile optimiert */}
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 line-clamp-2 group-hover:text-orange-600 transition-colors leading-tight">
            {product.name}
          </h3>

          {/* KORRIGIERTE PREIS-ANZEIGE - Mobile optimiert */}
          <div className="mb-3 sm:mb-4">
            <div className="text-xl sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">
              CHF {displayPrice.toFixed(2)}
            </div>
            <div className="text-xs sm:text-sm text-gray-600 mb-1">pro Verpackungseinheit</div>
            
            {/* B2B-INFORMATIONEN - Mobile optimiert */}
            <div className="space-y-1 text-xs sm:text-sm">
              {/* Stück pro VE */}
              {safeVEQuantity > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Stück pro VE:</span>
                  <span className="font-medium text-gray-900">
                    {safeVEQuantity}
                  </span>
                </div>
              )}
              
              {/* Preis pro Stück */}
              {safePricePerPiece > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">
                    <span className="hidden sm:inline">Preis pro Stück:</span>
                    <span className="sm:hidden">Pro Stück:</span>
                  </span>
                  <span className="font-medium text-orange-600">
                    CHF {safePricePerPiece.toFixed(2)}
                  </span>
                </div>
              )}
              
              {/* VE pro Karton - nur anzeigen wenn > 0 */}
              {safeVEPerCarton && safeVEPerCarton > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">
                    <span className="hidden sm:inline">VE pro Karton:</span>
                    <span className="sm:hidden">VE/Karton:</span>
                  </span>
                  <span className="font-medium text-blue-600">
                    {safeVEPerCarton}
                  </span>
                </div>
              )}
              
              {/* Karton pro Palette - nur anzeigen wenn > 0 */}
              {safeCartonPerPallet && safeCartonPerPallet > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">
                    <span className="hidden sm:inline">Karton pro Palette:</span>
                    <span className="sm:hidden">K/Palette:</span>
                  </span>
                  <span className="font-medium text-purple-600">
                    {safeCartonPerPallet}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Mengenauswahl und Warenkorb - Mobile optimiert */}
          <div className="space-y-3">
            {/* Mengenauswahl - Mobile optimiert */}
            <div className="flex items-center justify-between">
              <span className="text-xs sm:text-sm font-medium text-gray-700">
                {hasB2BPricing ? 'Anzahl VE:' : 'Menge:'}
              </span>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleQuantityChange(quantity - 1)}
                  disabled={quantity <= minQuantity}
                  className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors cursor-pointer"
                >
                  <i className="ri-subtract-line text-xs sm:text-sm text-gray-600"></i>
                </button>
                
                <input
                  type="number"
                  min={minQuantity}
                  value={quantity}
                  onChange={(e) => handleQuantityChange(parseInt(e.target.value) || minQuantity)}
                  className="w-12 sm:w-16 text-center border border-gray-300 rounded px-1 sm:px-2 py-1 text-xs sm:text-sm"
                />
                
                <button
                  onClick={() => handleQuantityChange(quantity + 1)}
                  className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors cursor-pointer"
                >
                  <i className="ri-add-line text-xs sm:text-sm text-gray-600"></i>
                </button>
              </div>
            </div>

            {/* Gesamtpreis Vorschau - Mobile optimiert */}
            <div className="bg-gray-50 rounded-lg p-2 sm:p-3">
              <div className="flex items-center justify-between text-xs sm:text-sm">
                <span className="text-gray-600">Gesamtpreis:</span>
                <span className="font-bold text-gray-900">
                  CHF {(displayPrice * quantity).toFixed(2)}
                </span>
              </div>
              {hasB2BPricing && safeVEQuantity > 0 && (
                <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
                  <span>
                    <span className="hidden sm:inline">Gesamtstückzahl:</span>
                    <span className="sm:hidden">Total Stück:</span>
                  </span>
                  <span>{quantity * safeVEQuantity} Stück</span>
                </div>
              )}
            </div>

            {/* Warenkorb Button - Mobile optimiert */}
            <button
              onClick={handleAddToCart}
              disabled={isAdding || product.stock === 0 || (product.mindestmengeStueck && product.stock < product.mindestmengeStueck)}
              className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white py-2 sm:py-3 px-3 sm:px-4 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center justify-center space-x-2 whitespace-nowrap cursor-pointer text-sm sm:text-base"
            >
              {isAdding ? (
                <>
                  <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span className="hidden sm:inline">Wird hinzugefügt...</span>
                  <span className="sm:hidden">Hinzufügen...</span>
                </>
              ) : (
                <>
                  <i className="ri-shopping-cart-line text-sm sm:text-lg"></i>
                  <span className="hidden sm:inline">In den Warenkorb</span>
                  <span className="sm:hidden">Warenkorb</span>
                </>
              )}
            </button>

            {/* Mindestmenge Hinweis - Mobile optimiert */}
            {product.mindestmengeStueck && product.mindestmengeStueck > 1 && !hasB2BPricing && (
              <p className="text-xs text-gray-500 text-center">
                <span className="hidden sm:inline">Mindestbestellmenge: {product.mindestmengeStueck} Stück</span>
                <span className="sm:hidden">Min: {product.mindestmengeStueck} Stück</span>
              </p>
            )}
          </div>
        </div>
      </div>

      {/* BILD-MODAL für Vergrößerung - Mobile optimiert */}
      {showImageModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-2 sm:p-4" onClick={closeImageModal}>
          <div className="relative max-w-4xl max-h-[90vh] w-full h-full flex items-center justify-center">
            {/* Schließen Button - Mobile optimiert */}
            <button
              onClick={closeImageModal}
              className="absolute top-2 right-2 sm:top-4 sm:right-4 z-10 w-10 h-10 sm:w-12 sm:h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-colors cursor-pointer shadow-lg"
            >
              <i className="ri-close-line text-xl sm:text-2xl text-gray-700"></i>
            </button>
            
            {/* Vergrößertes Bild */}
            <img
              src={product.image}
              alt={product.name}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            
            {/* Produktname unter dem Bild - Mobile optimiert */}
            <div className="absolute bottom-2 sm:bottom-4 left-1/2 transform -translate-x-1/2 bg-white/90 px-3 sm:px-4 py-2 rounded-lg shadow-lg max-w-[90%]">
              <h3 className="text-sm sm:text-lg font-semibold text-gray-900 text-center line-clamp-2">
                {product.name}
              </h3>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
